/**
 * Community blog content for the /blog section.
 *
 * Data lives in blog.data.json — 30 short posts authored by the blog
 * expert-research workflow (planner + writers), normalised to ₹. This module
 * adds types + helpers. Bodies are simple Markdown (see src/lib/renderMarkdown).
 */
import data from './blog.data.json';

export interface BlogPost {
  slug: string;
  title: string;
  category: string;
  excerpt: string;
  tags: string[];
  readMins: number;
  body: string;
  /** Optional hero illustration (path under /public, e.g. /blog/<slug>.svg). */
  image?: string;
  /** Date the salary and fee figures in the body were last checked, e.g. "25 October 2026". */
  figuresCheckedOn?: string;
}

export const posts: BlogPost[] = data.posts as BlogPost[];

/** Distinct categories, in first-seen order. */
export const categories: string[] = posts.reduce<string[]>((acc, p) => {
  if (!acc.includes(p.category)) acc.push(p.category);
  return acc;
}, []);

export function getPost(slug: string): BlogPost | undefined {
  return posts.find((p) => p.slug === slug);
}

/** Up to `n` other posts, preferring the same category. */
export function relatedPosts(slug: string, n = 3): BlogPost[] {
  const post = getPost(slug);
  if (!post) return [];
  const sameCat = posts.filter((p) => p.slug !== slug && p.category === post.category);
  const rest = posts.filter((p) => p.slug !== slug && p.category !== post.category);
  return [...sameCat, ...rest].slice(0, n);
}
